/**
 * Store API Service
 * Handles all API calls for the e-commerce store
 */

import api from '../../services/api/axiosConfig';

const storeApi = {
  // ==================== Categories ====================
  
  /**
   * Get all categories
   * @param {Object} params - Query parameters (parent_only, child_only, parent, search)
   */
  getCategories: async (params = {}) => {
    try {
      const response = await api.get('/store/categories/', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  },

  /**
   * Get parent categories only
   */
  getParentCategories: async () => {
    try {
      const response = await api.get('/store/categories/', { 
        params: { parent_only: 'true' } 
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching parent categories:', error);
      throw error;
    }
  },

  /**
   * Get category by ID
   */
  getCategory: async (id) => {
    try {
      const response = await api.get(`/store/categories/${id}/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching category:', error);
      throw error;
    }
  },

  // ==================== Products ====================
  
  /**
   * Get all products with filters
   * @param {Object} params - Query parameters (category, min_price, max_price, in_stock, search, ordering, page, page_size)
   */
  getProducts: async (params = {}) => {
    try {
      const response = await api.get('/store/products/', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching products:', error);
      throw error;
    }
  },

  /**
   * Get product by slug
   */
  getProduct: async (slug) => {
    try {
      const response = await api.get(`/store/products/${slug}/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching product:', error);
      throw error;
    }
  },

  /**
   * Get featured products
   * @param {string} type - 'featured', 'new_arrival', 'bestseller', 'sale'
   */
  getFeaturedProducts: async (type = 'featured') => {
    try {
      const response = await api.get('/store/products/featured/', {
        params: { type }
      });
      // Backend uses ApiResponse: { success, message, data }
      return response.data?.data ?? response.data;
    } catch (error) {
      console.error('Error fetching featured products:', error);
      throw error;
    }
  },

  /**
   * Create a new product (requires authentication)
   */
  createProduct: async (productData) => {
    try {
      const response = await api.post('/store/products/', productData);
      return response.data;
    } catch (error) {
      console.error('Error creating product:', error);
      throw error;
    }
  },

  // ==================== Manufacturers ====================
  
  /**
   * Get all manufacturers
   */
  getManufacturers: async () => {
    try {
      const response = await api.get('/store/manufacturers/');
      return response.data;
    } catch (error) {
      console.error('Error fetching manufacturers:', error);
      throw error;
    }
  },

  // ==================== Cart ====================
  
  /**
   * Get user's cart (requires authentication)
   */
  getCart: async () => {
    try {
      const response = await api.get('/store/cart/');
      return response.data;
    } catch (error) {
      // Don't log error if user is simply not authenticated
      if (error.status !== 401 && !error.isAuthRequired) {
        console.error('Error fetching cart:', error);
      }
      throw error;
    }
  },

  /**
   * Add item to cart (requires authentication)
   * @param {number} productId - Product ID
   * @param {number} quantity - Quantity to add
   */
  addToCart: async (productId, quantity = 1) => {
    try {
      const response = await api.post('/store/cart/items/', {
        product_id: productId,
        quantity
      });
      return response.data;
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    }
  },

  /**
   * Update cart item quantity (requires authentication)
   * @param {number} itemId - Cart item ID
   * @param {number} quantity - New quantity
   */
  updateCartItem: async (itemId, quantity) => {
    try {
      const response = await api.patch(`/store/cart/items/${itemId}/`, {
        product_id: null, // Not needed for update
        quantity
      });
      return response.data;
    } catch (error) {
      console.error('Error updating cart item:', error);
      throw error;
    }
  },

  /**
   * Remove item from cart (requires authentication)
   */
  removeFromCart: async (itemId) => {
    try {
      await api.delete(`/store/cart/items/${itemId}/`);
      return true;
    } catch (error) {
      console.error('Error removing from cart:', error);
      throw error;
    }
  },

  // ==================== Wishlist ====================
  
  /**
   * Get user's wishlist (requires authentication)
   */
  getWishlist: async () => {
    try {
      const response = await api.get('/store/wishlist/');
      return response.data;
    } catch (error) {
      // Don't log error if user is simply not authenticated
      if (error.status !== 401 && !error.isAuthRequired) {
        console.error('Error fetching wishlist:', error);
      }
      throw error;
    }
  },

  /**
   * Add product to wishlist (requires authentication)
   */
  addToWishlist: async (productId) => {
    try {
      const response = await api.post('/store/wishlist/', {
        product_id: productId
      });
      return response.data;
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      throw error;
    }
  },

  /**
   * Remove product from wishlist (requires authentication)
   */
  removeFromWishlist: async (wishlistItemId) => {
    try {
      await api.delete(`/store/wishlist/${wishlistItemId}/`);
      return true;
    } catch (error) {
      console.error('Error removing from wishlist:', error);
      throw error;
    }
  },

  // ==================== Orders ====================
  
  /**
   * Get user's orders (requires authentication)
   */
  getOrders: async () => {
    try {
      const response = await api.get('/store/orders/');
      return response.data;
    } catch (error) {
      console.error('Error fetching orders:', error);
      throw error;
    }
  },

  /**
   * Get order by ID (requires authentication)
   */
  getOrder: async (orderId) => {
    try {
      const response = await api.get(`/store/orders/${orderId}/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching order:', error);
      throw error;
    }
  },

  /**
   * Create order from cart (requires authentication)
   * @param {Object} shippingData - Shipping information
   */
  createOrder: async (shippingData) => {
    try {
      const response = await api.post('/store/orders/', shippingData);
      return response.data;
    } catch (error) {
      console.error('Error creating order:', error);
      throw error;
    }
  },

  // ==================== Product Reviews ====================
  
  /**
   * Get reviews for a product
   * @param {number} productId - Product ID
   */
  getProductReviews: async (productId) => {
    try {
      const response = await api.get('/store/reviews/', {
        params: { product: productId }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching reviews:', error);
      throw error;
    }
  },

  /**
   * Create a product review (requires authentication)
   */
  createReview: async (reviewData) => {
    try {
      const response = await api.post('/store/reviews/', reviewData);
      return response.data;
    } catch (error) {
      console.error('Error creating review:', error);
      throw error;
    }
  },
};

export default storeApi;

